import { api } from "@src/utils/axios-instance";
import Button from "../Button/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import "./styles/friendrequest.sass";
import { config, easings, useSpring, animated } from "@react-spring/web";
import { FriendRequest as FriendRequestType } from "@src/types/global";

interface IProps {
    request: FriendRequestType;
}
/*
 * Renders a friend request, handles accept/ reject and block functionalities
 * @param request - The request item to display
 */
const FriendRequest = ({ request }: IProps) => {
    async function updateRequest({ value }: { value: string }) {
        await api.patch("/friends/requests/" + request.id, { _action: value });
    }
    async function blockUser({ id }: { id: string }) {
        await api.post("/blocks/", { block: { blocked_user_id: id } });
    }
    const queries = useQueryClient();
    const updateRequestMutation = useMutation({
        mutationFn: updateRequest,
        mutationKey: ["friendRequests"],
        onMutate: () => {
            springApi.start({
                to: {
                    opacity: 0,
                    maxHeight: 0,
                    padding: "0rem",
                    marginBottom: "0rem",
                },
            });
        },
        onSuccess: (_data, variables) => {
            queries.invalidateQueries({ queryKey: ["friendRequests"] });
            queries.invalidateQueries({ queryKey: ["friends"] });
            queries.invalidateQueries({ queryKey: ["blocks"] });
            if (variables.value == "accept") {
                toast.success("Friend request accepted");
            } else {
                toast.success("Friend request rejected");
            }
        },
    });

    const blockUserMutation = useMutation({
        mutationFn: blockUser,
        mutationKey: ["blocks"],
    });

    //Animation
    const [props, springApi] = useSpring(
        () => ({
            from: {
                opacity: 1,
                maxHeight: 200,
                padding: "1rem",
                marginBottom: "1.5rem",
            },
            config: { ...config.stiff, easing: easings.easeInOutCirc },
        }),
        [],
    );

    return (
        <animated.div style={props} className="friend-request">
            <p className="friend-request_text">
                {request.requesting_user.username} sent you a friend request
            </p>
            <div className="friend-request_buttons">
                <Button
                    preset="primary"
                    value={"accept"}
                    fn={(e: React.PointerEvent<HTMLButtonElement>) =>
                        updateRequestMutation.mutate({
                            value: e.currentTarget.value,
                        })
                    }
                >
                    Accept
                </Button>
                <Button
                    preset="stealth"
                    value={"reject"}
                    fn={(e: React.PointerEvent<HTMLButtonElement>) =>
                        updateRequestMutation.mutate({
                            value: e.currentTarget.value,
                        })
                    }
                >
                    Reject
                </Button>
                <Button
                    preset="danger"
                    fn={() =>
                        blockUserMutation.mutate({
                            id: request.requesting_user_id,
                        })
                    }
                >
                    Block
                </Button>
            </div>
        </animated.div>
    );
};
export default FriendRequest;
